import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Phone, Mail, User, Plus, Trash2, Shield, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Trusted Contacts Manager
export const TrustedContactsManager = ({ token, onUpdate }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    phone: '',
    email: '',
    relationship: 'family',
    is_primary: false
  });

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const res = await fetch(`${API}/safety/trusted-contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setContacts(data.contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveContacts = async (updatedContacts) => {
    try {
      const res = await fetch(`${API}/safety/trusted-contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ contacts: updatedContacts })
      });
      const data = await res.json();
      if (data.success) {
        setContacts(data.contacts);
        toast.success('Trusted contacts updated');
        onUpdate?.();
      }
    } catch (error) {
      toast.error('Failed to update contacts');
    }
  };

  const addContact = () => {
    if (!newContact.name || !newContact.phone) {
      toast.error('Name and phone are required');
      return;
    }
    if (contacts.length >= 3) {
      toast.error('Maximum 3 contacts allowed');
      return;
    }
    const isPrimary = contacts.length === 0;
    const updated = [...contacts, { ...newContact, is_primary: isPrimary }];
    saveContacts(updated);
    setNewContact({ name: '', phone: '', email: '', relationship: 'family', is_primary: false });
    setShowAdd(false);
  };

  const removeContact = (index) => {
    if (contacts.length <= 1) {
      toast.error('At least one trusted contact is required');
      return;
    }
    const updated = contacts.filter((_, i) => i !== index);
    if (contacts[index].is_primary && updated.length > 0) {
      updated[0].is_primary = true;
    }
    saveContacts(updated);
  };

  const setPrimary = (index) => {
    const updated = contacts.map((c, i) => ({ ...c, is_primary: i === index }));
    saveContacts(updated);
  };

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Shield className="w-5 h-5 text-[#1E4DB7]" />
          Trusted Contacts
        </CardTitle>
        <p className="text-sm text-slate-500">
          These contacts will be notified in case of emergency (1 required, up to 3)
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {contacts.length === 0 && (
          <div className="text-center py-6 bg-amber-50 rounded-xl border border-amber-200">
            <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <p className="text-amber-700 font-medium">No trusted contacts set</p>
            <p className="text-sm text-amber-600">Add at least one contact for your safety</p>
          </div>
        )}

        {contacts.map((contact, index) => (
          <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
            <div className="w-10 h-10 bg-[#1E4DB7] rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{contact.name}</span>
                {contact.is_primary && (
                  <Badge className="bg-[#D4AF37] text-xs">Primary</Badge>
                )}
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Phone className="w-3 h-3" /> {contact.phone}
                </span>
                {contact.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {contact.email}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {!contact.is_primary && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPrimary(index)}
                  className="text-[#1E4DB7]"
                >
                  Set Primary
                </Button>
              )}
              <Button
                size="icon"
                variant="ghost"
                onClick={() => removeContact(index)}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}

        {contacts.length < 3 && (
          <Button
            variant="outline"
            className="w-full rounded-xl border-dashed"
            onClick={() => setShowAdd(true)}
          >
            <Plus className="w-4 h-4 mr-2" /> Add Trusted Contact
          </Button>
        )}

        {/* Add Contact Dialog */}
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Trusted Contact</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={newContact.name}
                  onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                  placeholder="Contact name"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>
              <div className="space-y-2">
                <Label>Email (optional)</Label>
                <Input
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  placeholder="Email address"
                />
              </div>
              <div className="space-y-2">
                <Label>Relationship</Label>
                <Select
                  value={newContact.relationship}
                  onValueChange={(v) => setNewContact({ ...newContact, relationship: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="family">Family</SelectItem>
                    <SelectItem value="friend">Friend</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={addContact} className="bg-[#1E4DB7]">Add Contact</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

// Panic Button Component
export const PanicButton = ({ rideId, token, onAlert }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [alertSent, setAlertSent] = useState(false);
  const [alertId, setAlertId] = useState(null);

  const triggerPanic = async () => {
    try {
      // Get current location
      let location = null;
      if (navigator.geolocation) {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        }).catch(() => null);
        if (pos) {
          location = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        }
      }

      const res = await fetch(`${API}/safety/panic-alert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ride_id: rideId,
          location,
          silent: true
        })
      });

      const data = await res.json();
      if (data.success) {
        setAlertSent(true);
        setAlertId(data.alert_id);
        onAlert?.(data);
        toast.success('Emergency alert sent to your trusted contacts');
      }
    } catch (error) {
      toast.error('Failed to send alert. Please call 911 directly.');
    }
    setShowConfirm(false);
  };

  const cancelAlert = async () => {
    if (!alertId) return;
    try {
      await fetch(`${API}/safety/panic-alert/${alertId}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlertSent(false);
      setAlertId(null);
      toast.info('Alert cancelled');
    } catch (error) {
      console.error('Error cancelling alert:', error);
    }
  };

  if (alertSent) {
    return (
      <div className="p-4 bg-red-50 rounded-xl border border-red-200">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
            <AlertTriangle className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-red-700">Emergency Alert Active</p>
            <p className="text-sm text-red-600">Help is on the way. Your contacts have been notified.</p>
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={cancelAlert}
            className="text-red-600 border-red-300"
          >
            Cancel (False Alarm)
          </Button>
          <a href="tel:911" className="flex-1">
            <Button size="sm" className="w-full bg-red-600 hover:bg-red-700">
              <Phone className="w-4 h-4 mr-2" /> Call 911
            </Button>
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <Button
        data-testid="panic-button"
        onClick={() => setShowConfirm(true)}
        className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl h-14 text-lg font-bold"
      >
        <AlertTriangle className="w-5 h-5 mr-2" /> Emergency SOS
      </Button>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Emergency Alert
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              This will silently alert your trusted contacts and DELEVR safety team with your location.
              The driver will NOT be notified.
            </p>
            <p className="mt-3 text-sm text-slate-500">
              If you're in immediate danger, call 911 directly.
            </p>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button onClick={triggerPanic} className="w-full bg-red-600 hover:bg-red-700">
              Send Silent Alert
            </Button>
            <a href="tel:911" className="w-full">
              <Button variant="outline" className="w-full border-red-300 text-red-600">
                Call 911 Instead
              </Button>
            </a>
            <Button variant="ghost" onClick={() => setShowConfirm(false)} className="w-full">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Driver Selfie Verification Component
export const DriverSelfieVerification = ({ token, onVerified }) => {
  const [capturing, setCapturing] = useState(false);
  const [verified, setVerified] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(true);

  useEffect(() => {
    checkVerificationStatus();
  }, []);

  const checkVerificationStatus = async () => {
    try {
      const res = await fetch(`${API}/driver/verification-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setNeedsVerification(data.needs_verification);
      setVerified(!data.needs_verification);
    } catch (error) {
      console.error('Error checking verification:', error);
    }
  };

  const handleCapture = async () => {
    setCapturing(true);
    
    try {
      // In production, this would capture from camera
      // For now, simulate with a placeholder
      const mockSelfieData = "base64_selfie_data_placeholder";
      
      const res = await fetch(`${API}/driver/selfie-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ selfie_data: mockSelfieData })
      });

      const data = await res.json();
      if (data.success) {
        setVerified(true);
        setNeedsVerification(false);
        toast.success('Identity verified! You can now accept rides.');
        onVerified?.();
      }
    } catch (error) {
      toast.error('Verification failed. Please try again.');
    } finally {
      setCapturing(false);
    }
  };

  if (!needsVerification && verified) {
    return (
      <div className="flex items-center gap-2 p-3 bg-green-50 rounded-xl border border-green-200">
        <CheckCircle className="w-5 h-5 text-green-600" />
        <span className="text-green-700 font-medium">Identity Verified</span>
      </div>
    );
  }

  return (
    <Card className="border-amber-200 bg-amber-50">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-amber-800">Selfie Verification Required</h3>
            <p className="text-sm text-amber-700 mt-1">
              Take a selfie to verify your identity before starting your shift.
            </p>
            <Button
              onClick={handleCapture}
              disabled={capturing}
              className="mt-3 bg-[#1E4DB7] hover:bg-[#163A8F]"
            >
              {capturing ? 'Verifying...' : 'Take Selfie'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default { TrustedContactsManager, PanicButton, DriverSelfieVerification };
